import { defineStore } from "pinia";
import { useSecurity } from "./security";
import { fetchService } from "../utils/fetchService";
import { ALARM_TYPES_DB, AlarmOrderBy, BREADCRUMB_FILTER_BY_ENUM, SortDTO } from "../utils/type.util";
import { IAlarm, IAlarmsProp, ICountersFilter } from "../interfaces/IAlarm";
import { store as HelpStore } from "../components/Help/Help.store";
import { IDeviceGroupsFilterDTO } from "../dtos/deviceGroupsFilter.dto";
import { IDeviceGroups } from "../dtos/deviceGroups.dto";

import i18n from "../i18n/i18n";

const fetchApi = async function (method: string, url: string, body: any) {
  let security = await useSecurity();

  const response = await fetchService(security)(url, {
    method: method || "GET",
    body: body,
    headers:
      method === "POST" || "PUT"
        ? { "Content-type": "application/json" }
        : undefined,
  });

  if (!response.ok) {
    if (response.status >= 400 && response.status < 499) {
      let { errorCode, errorContext } = await response.json();
      console.log(errorCode, errorContext);
      // serviceError.setErrorText(this.$t(`errors.${errorCode}`, errorContext))
    } else {
      //serviceError.setErrorText(this.$t('errorOccursRetry'))
    }

    return;
  }

  if (response.headers.get("content-length") === "0") {
    return;
  }

  return await response.json();
};

export const dataStore = defineStore("dataStore", {
  state: () => ({
    autocompleteItems: [],
    filters: {},
    editAlarmId: '',
    counterAlarmId: '',
    alarms: {
      content: [] as IAlarm[],
      filters: {
        orderBy: AlarmOrderBy.name,
        orderDir: SortDTO.ASC,
      },
      size: 10,
      number: 1,
      totalPages: 0,
      totalElements: 0,
    } as IAlarmsProp,
    showCreatePanel: false,
    showAdvancedPanel: false,
    showStateMatchine: false,
    showedState: "",
    showEditPanel: false,
    showCountersPanel: false,
    deviceGroups: [] as IDeviceGroups[],
    ALL_DEVICES_GROUP_NAME: '*',
    ALL_DEVICES_GROUP_LABEL: i18n.global.t('allDevices'),
  }),
  getters: {
    getAutocompleteItems: (state) => state.autocompleteItems,
    getFilters: (state) => state.filters,
    getAlarms: (state) => state.alarms,
    getShowCreatePanel: (state) => state.showCreatePanel,
    getShowAdvancedPanel: (state) => state.showAdvancedPanel,
    getShowStateMachine: (state) => state.showStateMatchine,
    getShowedState: (state) => state.showedState,
    getShowEditPanel: (state) => state.showEditPanel,
    getEditAlarmId: (state) => state.editAlarmId,
    getShowCountersPanel: (state) => state.showCountersPanel,
    getDeviceGroups: (state) => state.deviceGroups,
    getAllDevicesGroupName: (state) => state.ALL_DEVICES_GROUP_NAME,
    getAllDevicesGroupLabel: (state) => state.ALL_DEVICES_GROUP_LABEL,
  },
  actions: {
    openCreate($event: any) {
      $event.stopImmediatePropagation();
      this.showCreatePanel = true;
    },
    async openEdit($event: any) {
      $event.stopImmediatePropagation();
      this.showEditPanel = true;
    },
    openCounters($event: any) {
      $event.stopImmediatePropagation();
      this.showCountersPanel = true;
    },
    closeCounters($event: any) {
      $event.stopImmediatePropagation();
      this.showCountersPanel = false;
    },
    closeCreate($event: any) {
      $event.stopImmediatePropagation();
      this.showStateMatchine = false;
      this.showedState = "";
      this.showCreatePanel = false;
      HelpStore.drawerItems["HelpSideDrawer"]?.close($event);
    },
    closeEdit($event: any) {
      $event.stopImmediatePropagation();
      this.showStateMatchine = false;
      this.showedState = "";
      this.showEditPanel = false;
      HelpStore.drawerItems["HelpEditSideDrawer"]?.close($event);
    },
    openAdvanced($event: any) {
      $event.stopImmediatePropagation();
      this.closeCreate($event);
      this.showAdvancedPanel = true;
    },
    closeAdvanced($event: any) {
      $event.stopImmediatePropagation();
      this.closeCreate($event);
      this.showAdvancedPanel = false;
    },
    closeStateMachine($event: any) {
      $event.stopImmediatePropagation();
      this.showStateMatchine = false;
      this.showedState = "";
    },
    toggleStateMachine($event: any, type: any) {
      if (this.showStateMatchine) {
        this.closeStateMachine($event);
      } else {
        $event.stopImmediatePropagation();
        this.showStateMatchine = true;
        this.showedState = type.id;
        HelpStore.drawerItems["HelpSideDrawer"]?.close($event);
        HelpStore.drawerItems["HelpEditSideDrawer"]?.close($event);
      }
    },
    toggleShowedState(type: any) {
      this.showedState = type.id;
    },
    async fetchFilters() {
      this.filters = {
        alarms: [
          {
            title: "Name",
            value: {
              key: "name",
              filterByMode: BREADCRUMB_FILTER_BY_ENUM.FREE_TEXT,
            },
          },
          {
            title: "Type",
            value: {
              key: "type",
              filterByMode: BREADCRUMB_FILTER_BY_ENUM.AUTOCOMPLETE,
              autocompleteFunction: this.alarmTypeFilters,
            },
          },
        ],
      };
    },
    async alarmTypeFilters(_) {
      this.autocompleteItems = ALARM_TYPES_DB;
    },
    async fetchAlarms(input) {
      const filters = {
        orderBy: input.filters.orderBy,
        orderDir: input.filters.orderDir,
        page: input.page,
        pageSize: input.pageSize,
      };
      const queryParam = new URLSearchParams(filters);
      if (input.filters.name) {
        for (const n of input.filters.name) {
          queryParam.append("name", n);
        }
      }
      if (input.filters.type) {
        for (const s of input.filters.type) {
          const alarmType = ALARM_TYPES_DB.filter(at => at.id == s)
          queryParam.append("type", alarmType[0].id);
        }
      }
      if (input.filters.search) {
        queryParam.append("search", input.filters.search);
      }
      const alarms = await fetchApi("GET", "alarms?" + queryParam, undefined);

      this.alarms = {
        content: alarms.content,
        totalPages: alarms.totalPages,
        totalElements: alarms.totalElements,
        size: alarms.size,
      };
    },
    async fetchAlarm(alarmId) {
      let security = await useSecurity();
      const alarm = await fetchApi("GET", "alarms/" + alarmId, undefined);
      return {
        ...alarm,
        instanceId: security.instanceId,
        organizationId: security.connect?.organizationId,
      };
    },
    async fetchTransitions(alarmId) {
      let security = await useSecurity();
      const alarm = await fetchApi("GET", "alarms/" + alarmId + "/transitions", undefined);
      return {
        ...alarm,
        instanceId: security.instanceId,
        organizationId: security.connect?.organizationId,
      };
    },
    async fetchCounters(input) {
      const filters = {
        orderBy: input.filters.orderBy,
        orderDir: input.filters.orderDir,
        page: input.page,
        pageSize: input.pageSize,
      };
      const queryParam = new URLSearchParams(filters);
      if (input.period) {
        queryParam.append("period", input.period);
      }
       if (input.transitionId) {
        queryParam.append("transitionId", input.transitionId);
      }
      const counters = await fetchApi("GET", "alarms/" + input.alarmId + "/counters?" + queryParam, undefined);
      return counters;
    },
    async fetchModels(filters) {
      const queryParam = new URLSearchParams(filters);
      return await fetchApi("GET", "devices/models?" + queryParam, undefined);
    },
    async fetchDeviceGroups(filter: IDeviceGroupsFilterDTO): Promise<IDeviceGroups[]> {
      const queryParam = new URLSearchParams();
      queryParam.append("page", filter.page.toString());
      queryParam.append("pageSize", filter.pageSize.toString());
      if (filter.name) {
        // name is translated in UI, need to convert back to actual name
        const nameForQuery = (filter.name === this.ALL_DEVICES_GROUP_LABEL) ? this.ALL_DEVICES_GROUP_NAME : filter.name;
        queryParam.append("name", nameForQuery);
      }
      const response = await fetchApi("GET", "devices/groups?" + queryParam, undefined);
      response.content.forEach((dg: IDeviceGroups) => {
        // add label for UI purposes, translating '*' to 'All Devices'
        dg.label = (dg.name === this.ALL_DEVICES_GROUP_NAME) ? this.ALL_DEVICES_GROUP_LABEL : dg.name;
      });
      return response.content;
    },
    async initDeviceGroups(): Promise<IDeviceGroups[]> {
      this.deviceGroups = await this.fetchDeviceGroups({ page: 0, pageSize: 10000 });
      return this.deviceGroups;
    },
    async createAlarm(alarm) {
      return await fetchApi("POST", "alarms", JSON.stringify(alarm));
    },
    async updateAlarm(alarm) {
      return await fetchApi(
        "PUT",
        "alarms/" + alarm.alarmId,
        JSON.stringify(alarm)
      );
    },
    async deleteConfigAlarm(id) {
      return await fetchApi("DELETE", "alarms/" + id, undefined);
    },
    async cloneConfigAlarm(id, body) {
      return await fetchApi("POST", "alarms/" + id + "/clone", JSON.stringify(body));
    },
  },
});
